import { useMemo, useRef, useState } from 'react'
import { AlertTriangle, Loader2, Pause, Play, Plus, Route, Smartphone, Square, X } from 'lucide-react'
import { SectionTitle } from '@/components/panels/SectionTitle'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import {
  formatDistance,
  formatDuration,
  formatTagId,
  labelAt,
  progressOf,
  summarise,
  waypointTicks,
} from '@/lib/journeys'
import type { Journey, Run, RunSink } from '@/types/journey'
import { isLive } from '@/types/journey'
import type { JourneyCapabilities, JourneyRelay } from '@/types/liveview'

export interface JourneyPanelProps {
  journeys: Journey[]
  total: number
  loading: boolean
  error: string | null
  onReload: () => void
  selected: Journey | null
  onSelect: (journey: Journey | null) => void
  capabilities: JourneyCapabilities | null
  runs: Run[]
  runError: string | null
  busy: boolean
  onStart: (options: {
    journeyId: string
    sink: RunSink
    relay?: string
    walker?: number
    rateHz: number
    speed: number
    loop: boolean
  }) => void
  onControl: (runId: string, action: 'pause' | 'resume' | 'stop') => void
  onDismissError: () => void
  /** A ticking clock, so progress moves without a per-second server message. */
  now: number
}

const SPEEDS = [1, 2, 5, 10]
const RATES = [1, 2, 4, 10]

/** The wristband number, set apart and grouped. Never what is sent anywhere. */
function TagNumber({ tagId }: { tagId: string | null }) {
  return <span className="font-mono tabular-nums">{formatTagId(tagId)}</span>
}

/**
 * Where a run's positions are going, in one line: the relay, the walker *and*
 * its wristband — the slot is what an operator picked, and the number is what
 * the phone is configured with.
 */
function WalkerLine({ run }: { run: Run }) {
  if (run.sink !== 'relay') return <>preview</>
  return (
    <>
      {run.relay ?? 'relay'} · {run.walker !== null ? `walker ${run.walker}` : 'relay'} ·{' '}
      <TagNumber tagId={run.tag_id} />
    </>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-1 flex-col gap-1">
      <span className="text-muted-foreground text-[10px] uppercase tracking-wide">{label}</span>
      {children}
    </label>
  )
}

/**
 * Pick a journey and play it; watch everything this organisation is playing.
 *
 * A section like `CapturePanel`, not a tab: there is no editing here — the
 * editor is MapTap's and LiveView's proxy is GET-only — so a journey is picked,
 * drawn, and walked.
 *
 * There is no "play into" choice. With a relay configured a run goes into it,
 * and the loopback brings it back onto this map — so it is on the map *and* on
 * the relay, which is what anybody wanted from the choice. Without one, it is a
 * preview. The ground floor is not asked either: it is the relay's default, and
 * the "Connect your app" card still prints it. The API keeps both parameters;
 * `scripts/journey-run.mjs` can still send them.
 */
export function JourneyPanel(props: JourneyPanelProps) {
  const relays = props.capabilities?.relays ?? []
  const relayAvailable = relays.length > 0
  const sink: RunSink = relayAvailable ? 'relay' : 'preview'

  const [relayName, setRelayName] = useState<string | null>(null)
  const [walkerSlot, setWalkerSlot] = useState<number | null>(null)
  const [rateHz, setRateHz] = useState(4)
  const [speed, setSpeed] = useState(1)
  const [loop, setLoop] = useState(false)

  // Which relay this run goes into. The server decides the default and the
  // list; the browser never holds a relay's URL or either of its tokens.
  const relay = useMemo(
    () =>
      relays.find((candidate) => candidate.name === relayName) ??
      relays.find((candidate) => candidate.name === props.capabilities?.default_relay) ??
      relays[0] ??
      null,
    [relays, relayName, props.capabilities?.default_relay],
  )

  // Everything this organisation is playing, whichever session started it —
  // two operators share one run table, and this is where the second one sees
  // that walker 1 is taken.
  //
  // In the order the runs first appeared here, so a card never moves under the
  // operator's cursor. Not by `started_at`: that is when a run last entered
  // `running`, so pausing or resuming one would reshuffle the cards. Runs
  // already live when the page loads are seeded in their `started_at` order.
  const firstSeen = useRef(new Map<string, number>())
  const liveRuns = useMemo(() => {
    const live = props.runs.filter(isLive)
    const order = firstSeen.current
    const byStart = (a: Run, b: Run) =>
      new Date(a.started_at).getTime() - new Date(b.started_at).getTime()
    for (const run of [...live].sort(byStart)) {
      if (!order.has(run.id)) order.set(run.id, order.size)
    }
    return live.sort((a, b) => order.get(a.id)! - order.get(b.id)!)
  }, [props.runs])

  // Which walker plays which run, on the relay this form points at. Read off
  // the run table rather than the config's `busy`: the table is kept current
  // by the socket, the config only by the next snapshot, so a walker started a
  // second ago would otherwise still be offered.
  const playingOn = useMemo(() => {
    const bySlot = new Map<number, Run>()
    for (const run of liveRuns) {
      if (run.sink === 'relay' && run.relay === relay?.name && run.walker !== null) {
        bySlot.set(run.walker, run)
      }
    }
    return bySlot
  }, [liveRuns, relay])

  // Which wristband this run plays as. The operator picks a *walker*, never a
  // number: the mapping is the server's, and on a shared sandbox picking a
  // number would be picking somebody else's. The choice follows the first free
  // slot until it is made by hand — so a second browser session of the same
  // organisation, seeing walker 1 busy, offers walker 2 without being told.
  const walkers = useMemo(() => relay?.walkers ?? [], [relay])
  const freeWalker = useMemo(
    () => walkers.find((walker) => !playingOn.has(walker.slot)) ?? null,
    [walkers, playingOn],
  )
  const walker = useMemo(
    () =>
      walkers.find((candidate) => candidate.slot === walkerSlot && !playingOn.has(candidate.slot)) ??
      freeWalker,
    [walkers, walkerSlot, freeWalker, playingOn],
  )
  // Every walker busy: a relay run has nothing to play as.
  const walkersExhausted = sink === 'relay' && walkers.length > 0 && freeWalker === null

  const start = (journey: Journey, slot: number | undefined) =>
    props.onStart({
      journeyId: journey.id,
      sink,
      // Sent even when there is only one relay: which relay a run went into is
      // not a default the browser should leave to be guessed later.
      relay: sink === 'relay' ? (relay?.name ?? undefined) : undefined,
      // Likewise the walker: the server would pick the lowest free slot anyway,
      // but which wristband a run went out on is not something to work out
      // afterwards.
      walker: sink === 'relay' ? slot : undefined,
      rateHz,
      speed,
      loop,
    })

  const lastRun = useMemo(
    () => props.runs.find((run) => run.journey_id === props.selected?.id) ?? null,
    [props.runs, props.selected],
  )

  const summary = useMemo(
    () => (props.selected ? summarise(props.selected) : null),
    [props.selected],
  )

  const journeyById = useMemo(
    () => new Map(props.journeys.map((journey) => [journey.id, journey])),
    [props.journeys],
  )

  return (
    <div className="flex flex-col gap-2 px-3 py-3">
      <SectionTitle aside={props.loading && <Loader2 className="size-3 animate-spin" />}>
        Journeys
      </SectionTitle>

      {props.error && (
        <p className="text-muted-foreground text-[11px]">
          Could not load journeys.{' '}
          <button className="underline underline-offset-2" onClick={props.onReload}>
            Try again
          </button>
          <span className="text-muted-foreground/70 mt-1 block font-mono break-all">
            {props.error}
          </span>
        </p>
      )}

      {!props.loading && !props.error && props.journeys.length === 0 && (
        <p className="text-muted-foreground text-[11px]">
          This organisation has no journeys yet. They are drawn in MapTap and stored by Proximi.io;
          LiveView only picks and plays them.
        </p>
      )}

      {props.total > props.journeys.length && (
        <p className="text-muted-foreground text-[11px]">
          Showing {props.journeys.length} of {props.total}.
        </p>
      )}

      {liveRuns.length > 0 ? (
        <>
          {/* The whole story of every walk, not one card and a footnote: with
              two playing, the second was the one nobody could find. */}
          {liveRuns.map((run) => (
            <RunCard
              key={run.id}
              run={run}
              journey={journeyById.get(run.journey_id) ?? null}
              now={props.now}
              busy={props.busy}
              onControl={props.onControl}
            />
          ))}

          {/* A second walk without a second form: same relay, same speed and
              rate, the next free walker. A journey already playing is still
              offered — one walk on two wristbands is a real test. */}
          <Select
            value=""
            onValueChange={(value) => {
              const journey = journeyById.get(value)
              if (!journey) return
              props.onSelect(journey)
              start(journey, freeWalker?.slot)
            }}
            disabled={props.busy || walkersExhausted || props.journeys.length === 0}
          >
            <SelectTrigger
              size="sm"
              className="bg-card w-full"
              aria-label="Add another journey to play"
            >
              <span className="flex items-center gap-2">
                <Plus className="size-3.5" />
                <SelectValue placeholder="Add another journey to play" />
              </span>
            </SelectTrigger>
            <SelectContent>
              {props.journeys.map((journey) => (
                <SelectItem key={journey.id} value={journey.id}>
                  {journey.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {walkersExhausted && (
            <p className="text-muted-foreground text-[11px]">
              All {walkers.length} of this organisation&apos;s walkers are playing. End one to play
              another journey.
            </p>
          )}
        </>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <Route className="text-muted-foreground size-3.5 shrink-0" />
            <Select
              value={props.selected?.id ?? '__none'}
              onValueChange={(value) =>
                props.onSelect(value === '__none' ? null : (journeyById.get(value) ?? null))
              }
              disabled={props.loading || props.journeys.length === 0}
            >
              <SelectTrigger size="sm" className="bg-card w-full" aria-label="Journey">
                <SelectValue placeholder="Select a journey to play" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">Select a journey to play</SelectItem>
                {props.journeys.map((journey) => (
                  <SelectItem key={journey.id} value={journey.id}>
                    {journey.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {props.selected && summary && (
            <>
              <div className="text-muted-foreground flex items-center gap-2 text-[11px] tabular-nums">
                <span>{props.selected.waypoints?.length ?? 0} waypoints</span>
                <span>·</span>
                <span>{formatDistance(summary.distanceM)}</span>
                <span>·</span>
                <span>{formatDuration(summary.durationS)}</span>
                {summary.levels.length > 1 && (
                  <Badge variant="outline" className="ml-auto px-1 py-0 text-[10px]">
                    {summary.levels.length} floors
                  </Badge>
                )}
              </div>

              {sink === 'relay' && relays.length > 1 && (
                <Field label="Relay">
                  {/* Names only. The URL and the two tokens are the server's,
                      and a relay nobody configured is not a relay. */}
                  <Select value={relay?.name ?? ''} onValueChange={setRelayName}>
                    <SelectTrigger size="sm" className="bg-card w-full" aria-label="Relay">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {relays.map((candidate) => (
                        <SelectItem key={candidate.name} value={candidate.name}>
                          {candidate.name}
                          {candidate.connected ? '' : ' (not connected)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}

              <div className="flex gap-2">
                {sink === 'relay' && (
                  <Field label="Wristband">
                    {/* Walkers, not numbers. The block is leased by the server
                        from the organisation id, so walker 2 is the same
                        wristband next week and a phone is configured once —
                        one block per relay, because each is its own id space. */}
                    <Select
                      value={walker ? String(walker.slot) : ''}
                      onValueChange={(value) => setWalkerSlot(Number(value))}
                      disabled={walkers.length === 0}
                    >
                      <SelectTrigger size="sm" className="bg-card w-full" aria-label="Wristband">
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        {walkers.map((candidate) => (
                          <SelectItem
                            key={candidate.slot}
                            value={String(candidate.slot)}
                            disabled={playingOn.has(candidate.slot)}
                          >
                            <span>
                              Walker {candidate.slot} · <TagNumber tagId={candidate.tag_id} />
                            </span>
                            {playingOn.has(candidate.slot) ? ' (in use)' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                )}
                <Field label="Speed">
                  <Select value={String(speed)} onValueChange={(value) => setSpeed(Number(value))}>
                    <SelectTrigger size="sm" className="bg-card w-full" aria-label="Speed">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SPEEDS.map((value) => (
                        <SelectItem key={value} value={String(value)}>
                          {value}×
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <div className="flex items-center gap-2">
                <Field label="Rate">
                  <Select value={String(rateHz)} onValueChange={(value) => setRateHz(Number(value))}>
                    <SelectTrigger size="sm" className="bg-card w-full" aria-label="Rate">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RATES.map((value) => (
                        <SelectItem key={value} value={String(value)}>
                          {value} Hz
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <label
                  htmlFor="journey-loop"
                  className="flex cursor-pointer items-center gap-2 self-end pb-1.5"
                >
                  <Switch id="journey-loop" checked={loop} onCheckedChange={setLoop} />
                  <span className="text-xs font-medium">Loop</span>
                </label>
              </div>

              <Button
                size="sm"
                className="w-full"
                disabled={props.busy || walkersExhausted}
                onClick={() => start(props.selected!, walker?.slot)}
              >
                {props.busy ? <Loader2 className="animate-spin" /> : <Play />}
                Play
              </Button>

              {lastRun && lastRun.state !== 'finished' && (
                <p className="text-muted-foreground text-[11px]">
                  Last run {lastRun.state}
                  {lastRun.error ? `: ${lastRun.error}` : '.'}
                </p>
              )}
            </>
          )}
        </>
      )}

      {props.runError && (
        <div className="border-destructive/40 bg-destructive/10 flex items-start gap-2 rounded-md border px-2 py-1.5">
          <AlertTriangle className="text-destructive mt-0.5 size-3.5 shrink-0" />
          <p className="text-destructive min-w-0 flex-1 text-[11px]">{props.runError}</p>
          <button
            onClick={props.onDismissError}
            aria-label="Dismiss"
            className="text-destructive/70 hover:text-destructive"
          >
            <X className="size-3.5" />
          </button>
        </div>
      )}

      {relay && <ConnectYourApp relay={relay} playingOn={playingOn} journeyById={journeyById} />}
    </div>
  )
}

function RunCard({
  run,
  journey,
  now,
  busy,
  onControl,
}: {
  run: Run
  /** Null when the journey is not in the loaded list — the run still plays. */
  journey: Journey | null
  now: number
  busy: boolean
  onControl: (runId: string, action: 'pause' | 'resume' | 'stop') => void
}) {
  const progress = progressOf(run, now)
  const ticks = useMemo(() => waypointTicks(run), [run])
  const label = labelAt(journey, progress.leg)

  return (
    <div className="bg-muted/30 flex flex-col gap-2 rounded-md border px-2.5 py-2">
      <div className="flex items-center gap-2">
        <Route className="text-muted-foreground size-3.5 shrink-0" />
        {/* The name as text: what is playing is not something to re-pick. */}
        <span className="min-w-0 flex-1 truncate text-sm font-medium">{run.name}</span>
        <Badge variant={run.state === 'running' ? 'success' : 'warning'} className="text-[10px]">
          {run.state}
        </Badge>
      </div>

      <p className="text-muted-foreground text-[11px] tabular-nums">
        <WalkerLine run={run} /> · {run.speed}× · {run.rate_hz} Hz
        {run.loop ? ' · loop' : ''}
      </p>

      <div className="relative">
        <div className="bg-muted h-1.5 overflow-hidden rounded-full">
          <div
            className="h-full rounded-full bg-violet-400 transition-[width] duration-300"
            style={{ width: `${Math.round(progress.ratio * 100)}%` }}
          />
        </div>
        {/* Waypoint ticks: where the walker stops being between two places and
            starts being at one. */}
        {ticks.map((tick) => (
          <span
            key={tick}
            className="bg-background/70 absolute top-0 h-1.5 w-px"
            style={{ left: `${tick * 100}%` }}
          />
        ))}
      </div>

      <p className="text-muted-foreground text-[11px] tabular-nums">
        {progress.dwellingRemainingS !== null ? (
          <>
            Dwelling{label ? ` at ${label}` : ''} — {Math.ceil(progress.dwellingRemainingS)} s left
          </>
        ) : progress.leg?.level_change ? (
          <>Changing floor{label ? ` at ${label}` : ''}…</>
        ) : (
          <>
            {formatDuration(progress.elapsedS)} of {formatDuration(run.duration_s)}
            {progress.level !== null ? ` · level ${progress.level}` : ''}
          </>
        )}
      </p>

      <div className="flex gap-2">
        {run.state === 'running' ? (
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            disabled={busy}
            onClick={() => onControl(run.id, 'pause')}
          >
            <Pause />
            Pause
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            disabled={busy}
            onClick={() => onControl(run.id, 'resume')}
          >
            <Play />
            Resume
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          className="text-destructive hover:text-destructive border-destructive/40 hover:bg-destructive/10 flex-1"
          disabled={busy}
          onClick={() => onControl(run.id, 'stop')}
        >
          <Square />
          End Journey
        </Button>
      </div>
    </div>
  )
}

/**
 * The values that must agree on the phone, once for the whole section.
 *
 * Getting any one of them wrong is silent: the wrong relay shows nothing, the
 * wrong tag shows somebody else's walk, and the wrong ground floor puts every
 * position exactly one floor out. So it lists every walker this organisation
 * holds on the relay, not only the ones playing — a phone is set up for a
 * wristband before anything is played on it — and says which journey is on
 * which, so two phones can be told apart while they walk.
 *
 * The ground floor is the relay's default, the one a run is played with now
 * that the form no longer asks.
 */
function ConnectYourApp({
  relay,
  playingOn,
  journeyById,
}: {
  relay: JourneyRelay
  playingOn: Map<number, Run>
  journeyById: Map<string, Journey>
}) {
  return (
    <div className="bg-muted/40 flex flex-col gap-1 rounded-md border px-2 py-2">
      <div className="text-muted-foreground flex items-center gap-1.5 text-[10px] uppercase tracking-wide">
        <Smartphone className="size-3" />
        Connect your app
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-[11px]">
        <dt className="text-muted-foreground">Relay</dt>
        {/* The host is what the phone is configured with, so it is shown whole
            and wraps rather than truncates. */}
        <dd className="font-mono break-all">{relay.host}</dd>
        <dt className="text-muted-foreground">Ground floor</dt>
        <dd className="font-mono">{relay.default_ground_floor_no}</dd>
        {relay.walkers.map((walker) => {
          const run = playingOn.get(walker.slot)
          const name = run ? (journeyById.get(run.journey_id)?.name ?? run.name) : null
          return (
            <div key={walker.slot} className="contents">
              <dt className="text-muted-foreground">Walker {walker.slot}</dt>
              <dd className="min-w-0 truncate">
                <TagNumber tagId={walker.tag_id} />
                {name && <span className="text-muted-foreground"> · {name}</span>}
              </dd>
            </div>
          )
        })}
      </dl>
      <p className="text-muted-foreground text-[10px]">
        The relay, a walker&apos;s number and the ground floor must match what the phone is
        configured with. The app needs the relay&apos;s <span className="font-mono">stream</span>{' '}
        token, which cannot inject.
      </p>
    </div>
  )
}
